import { Component } from '@theme/component';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * Card-based package selector (Einzeln / Duo / Familienpaket).
 *
 * Variant mode: when a package variant matching the card exists, selecting a
 * card switches the product form to that variant (preferring a click on the
 * theme's own variant-picker radio so price, media and sticky bar update
 * natively) with quantity 1 — checkout then charges exactly the card price.
 *
 * Quantity mode (fallback, no matching variant): selecting a card sets the
 * real order quantity. Works with the visible quantity stepper if present,
 * otherwise via a hidden quantity input in the form (the stepper block can
 * be disabled in the editor).
 *
 * Free gifts & add-on:
 * - A card with `data-gift-property` writes a line-item property
 *   ("Gratis-Zugabe: 1× Schlafmaske") into the product form, so the gift is
 *   visible in cart, checkout and on the order.
 * - A card with `data-extra-items` (real 0-€ gift product) and/or a checked
 *   add-on checkbox triggers a second cart add right after the main add
 *   succeeds, using the theme's own cart events so drawer & icon update.
 *
 * @extends {Component}
 */
export class BundleSelectorComponent extends Component {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate, {
      signal: this.#abortController.signal,
    });

    // Sync the gift property for the option that is pre-selected on load.
    this.#syncGiftProperty();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  get #formComponent() {
    return document.querySelector(`product-form-component[data-section-id="${this.dataset.sectionId}"]`);
  }

  /** @returns {HTMLLabelElement | null} */
  get #checkedOption() {
    const radio = this.querySelector('.bundle-option__radio:checked');
    return radio ? radio.closest('label.bundle-option') : null;
  }

  /**
   * @param {{variantId?: string|number, optionValue?: string, quantity: number}} data
   */
  selectOption(data) {
    const formComponent = this.#formComponent;
    if (!formComponent) return;

    const sectionEl = formComponent.closest('.shopify-section') || document;
    const variantId = data.variantId ? String(data.variantId) : '';

    if (variantId) {
      // Prefer the theme's own picker radio: triggers native price/media updates.
      let pickerInput = /** @type {HTMLInputElement | null} */ (
        sectionEl.querySelector(`variant-picker input[type="radio"][data-variant-id="${variantId}"]`)
      );
      if (!pickerInput && data.optionValue) {
        const value = decodeURIComponent(String(data.optionValue));
        const radios = /** @type {NodeListOf<HTMLInputElement>} */ (
          sectionEl.querySelectorAll('variant-picker input[type="radio"]')
        );
        pickerInput = Array.from(radios).find((input) => input.value === value) ?? null;
      }

      if (pickerInput && !pickerInput.checked) {
        pickerInput.click();
      } else if (!pickerInput) {
        const idInput = /** @type {HTMLInputElement | null} */ (
          formComponent.querySelector('input[name="id"]')
        );
        if (idInput) idInput.value = variantId;
      }
      this.setQuantity(formComponent, 1);
    } else {
      this.setQuantity(formComponent, Number(data.quantity) || 1);
    }

    this.#syncGiftProperty();
  }

  /**
   * Keeps hidden gift property inputs in the product form in sync with the
   * selected bundle option. The visible property ("Gratis-Zugabe") travels
   * through cart, checkout and order confirmation; the underscore-prefixed
   * ones carry image/value for the theme's own cart gift row and stay
   * hidden everywhere else.
   */
  #syncGiftProperty() {
    const formComponent = this.#formComponent;
    if (!formComponent) return;

    const formEl =
      formComponent.querySelector('form[data-type="add-to-cart-form"]') || formComponent.querySelector('form');
    if (!formEl) return;

    const option = this.#checkedOption;
    const props = {
      'properties[Gratis-Zugabe]': option?.dataset.giftProperty || '',
      'properties[_gift_title]': option?.dataset.giftTitle || '',
      'properties[_gift_quantity]': option?.dataset.giftQuantity || '',
      'properties[_gift_image]': option?.dataset.giftImage || '',
      'properties[_gift_value]': option?.dataset.giftValue || '',
    };

    for (const [name, value] of Object.entries(props)) {
      let input = /** @type {HTMLInputElement | null} */ (
        formEl.querySelector(`input[name="${name}"][data-bundle-gift]`)
      );

      if (value) {
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = name;
          input.setAttribute('data-bundle-gift', '');
          formEl.appendChild(input);
        }
        input.value = value;
      } else if (input) {
        input.remove();
      }
    }
  }

  /**
   * Collects extra cart lines for the selected option: a real free-gift
   * product (data-extra-items) and/or the checked add-on product.
   * @returns {Array<{id: number, quantity: number, properties?: Record<string, string>}>}
   */
  #collectExtraItems() {
    const option = this.#checkedOption;
    if (!option) return [];

    /** @type {Array<{id: number, quantity: number, properties?: Record<string, string>}>} */
    const items = [];

    if (option.dataset.extraItems) {
      try {
        const parsed = JSON.parse(option.dataset.extraItems);
        if (Array.isArray(parsed)) {
          for (const item of parsed) {
            const id = Number(item?.id);
            const quantity = Number(item?.quantity) || 1;
            const properties =
              item?.properties && typeof item.properties === 'object' && !Array.isArray(item.properties)
                ? item.properties
                : undefined;
            if (id > 0) items.push({ id, quantity, properties });
          }
        }
      } catch {
        // Malformed JSON in the data attribute — ignore, main add still works.
      }
    }

    const sibling = option.nextElementSibling;
    const addonCard = sibling?.classList.contains('bundle-addon') ? sibling : null;
    const checkbox = /** @type {HTMLInputElement | null} */ (
      addonCard?.querySelector('.bundle-addon__checkbox') ?? null
    );
    if (checkbox?.checked && !checkbox.disabled) {
      const id = Number(checkbox.dataset.addonVariantId);
      if (id > 0) items.push({ id, quantity: 1 });
    }

    return items;
  }

  /**
   * After the main product was added from this section's form, add the
   * gift/add-on items as a follow-up cart add.
   * @param {CartLinesUpdateEvent} event
   */
  #onCartLinesUpdate = (event) => {
    if (event.action !== 'add') return;

    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (target.tagName.toLowerCase() !== 'product-form-component') return;
    if (target.dataset.sectionId !== this.dataset.sectionId) return;

    const extraItems = this.#collectExtraItems();
    if (extraItems.length === 0) return;

    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.#addExtraItems(extraItems);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[bundle-selector] Event promise rejected:', error);
      });
  };

  async #fetchCart() {
    /** @type {any} */
    const cartItemsComponent = document.querySelector('cart-items-component');
    if (cartItemsComponent) {
      await customElements.whenDefined('cart-items-component');
      return cartItemsComponent.fetchCartData();
    }

    return fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((response) => {
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
      return response.json();
    });
  }

  /**
   * Adds the extra items via the AJAX cart API, mirroring the theme's own
   * batch-add flow so drawer, icon and count update natively.
   * @param {Array<{id: number, quantity: number, properties?: Record<string, string>}>} items
   */
  #addExtraItems(items) {
    const sectionIds = [];
    for (const el of document.querySelectorAll('cart-items-component')) {
      if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
    }

    const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({
          merchandiseId: String(item.id),
          quantity: item.quantity,
        })),
        promise: deferredEventPromise.promise,
      })
    );

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ items, sections: sectionIds.join(',') }),
    })
      .then((response) => response.json())
      .then(async (response) => {
        const didError = Boolean(response.status);
        if (didError) {
          console.warn('[bundle-selector] Gift/add-on could not be added:', response.message || response);
        }

        const cart = await this.#fetchCart();
        deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            source: 'bundle-selector',
            sourceId: this.dataset.sectionId,
            itemCount: didError ? 0 : totalQuantity,
            sections: didError ? undefined : response.sections,
            didError,
          },
        });
      })
      .catch((error) => {
        console.warn('[bundle-selector] Gift/add-on add failed:', error);
        deferredEventPromise.reject(error);
      });
  }

  /**
   * Sets the order quantity on the product form, with or without a visible
   * quantity stepper.
   * @param {Element} formComponent
   * @param {number} quantity
   */
  setQuantity(formComponent, quantity) {
    const stepper = /** @type {any} */ (
      formComponent.querySelector('quantity-selector-component')
    );
    if (stepper && typeof stepper.setValue === 'function') {
      stepper.setValue(String(quantity));
      if (typeof stepper.onQuantityChange === 'function') stepper.onQuantityChange();
      if (typeof stepper.updateButtonStates === 'function') stepper.updateButtonStates();
      return;
    }

    const formEl =
      formComponent.querySelector('form[data-type="add-to-cart-form"]') ||
      formComponent.querySelector('form');
    if (!formEl) return;

    let input = /** @type {HTMLInputElement | null} */ (
      formEl.querySelector('input[name="quantity"]')
    );
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'quantity';
      formEl.appendChild(input);
    }
    input.value = String(quantity);
  }
}

if (!customElements.get('bundle-selector-component')) {
  customElements.define('bundle-selector-component', BundleSelectorComponent);
}
